import type { Process } from "@/models/flowchart";
import type { Project } from "@/models/project";
import type { Stakeholder } from "@/models/user";
import type { Glossary, Tab } from "@/models/workspace";
import { normalizeCollectionIds } from "@/utils/idGenerator";

type IdValue = string | number;

const hasUsableId = (value: unknown): value is IdValue => {
  return value !== undefined && value !== null && String(value).trim() !== "";
};

const toIdString = (value: unknown): string | undefined => {
  return hasUsableId(value) ? String(value) : undefined;
};

export function getNextProcessId(processes: Array<Partial<Process> | null | undefined>): string {
  const numericIds = processes
    .map((process) => Number(process?.id))
    .filter((id) => Number.isInteger(id) && id > 0);

  const maxProcessId = numericIds.length > 0 ? Math.max(...numericIds) : 0;
  return String(maxProcessId + 1);
}


export function normalizeProcessCollection(processes: unknown, allTabs?: Tab[]): Process[] {
  if (!Array.isArray(processes)) {
    return [];
  }

  const usedProcessIds = new Set<string>();
  let nextProcessId = 1;
  if (Array.isArray(allTabs)) {
    for (const tab of allTabs) {
      const tabProcesses = tab?.data?.processes;
      if (Array.isArray(tabProcesses)) {
        for (const proc of tabProcesses) {
          if (proc?.id && typeof proc.id === "string") {
            usedProcessIds.add(proc.id);
          }
        }
      }
    }
  }
  function getNextProcId(): string {
    let id;
    do {
      id = `PROC-${String(nextProcessId).padStart(3, "0")}`;
      nextProcessId++;
    } while (usedProcessIds.has(id));
    usedProcessIds.add(id);
    return id;
  }
  return (processes as Array<Partial<Process>>).map((process) => {
    const processId = typeof process?.id === "string" && /^PROC-\d{3}$/.test(process.id)
      ? process.id
      : getNextProcId();
    const usedStageIds = new Set<string>();
    let nextStageId = 1;
    const stages = Array.isArray(process?.stages) ? process.stages.map((stage) => {
      const stageId = typeof stage?.id === "string" && /^ACT-\d{3}$/.test(stage.id)
        ? stage.id
        : (() => {
            let id;
            do {
              id = `ACT-${String(nextStageId).padStart(3, "0")}`;
              nextStageId++;
            } while (usedStageIds.has(id));
            usedStageIds.add(id);
            return id;
          })();
      const usedTaskIds = new Set<string>();
      let nextTaskId = 1;
      const taskGroups = Array.isArray(stage?.taskGroups) ? stage.taskGroups.map((group) => {
        const tasks = Array.isArray(group?.tasks) ? group.tasks.map((task) => {
          const taskId = typeof task?.id === "string" && /^FR-\d{3}$/.test(task.id)
            ? task.id
            : (() => {
                let id;
                do {
                  id = `FR-${String(nextTaskId).padStart(3, "0")}`;
                  nextTaskId++;
                } while (usedTaskIds.has(id));
                usedTaskIds.add(id);
                return id;
              })();
          return {
            ...task,
            id: taskId,
          };
        }) : [];
        return {
          ...group,
          tasks,
        };
      }) : [];
      return {
        ...stage,
        id: stageId,
        taskGroups,
      };
    }) : [];
    return {
      ...process,
      id: processId,
      name: typeof process?.name === "string" ? process.name : "",
      stages,
    } as Process;
  });
}

type ProjectIdentityCarrier = {
  id?: IdValue;
  codicent?: string;
};

type FlowchartTab = { data?: { processes?: unknown } };
type WorkspaceSubmenu = { id?: string; tabs?: FlowchartTab[]; data?: any };

export function normalizeProjectIdentity<T extends ProjectIdentityCarrier>(
  project: T,
  fallbackProjectId?: string
): T & { id: string; codicent: string } {
  const canonicalProjectId =
    toIdString(fallbackProjectId) ?? toIdString(project.codicent) ?? toIdString(project.id);

  if (!canonicalProjectId) {
    throw new Error("Project is missing a canonical id/codicent value.");
  }

  return {
    ...project,
    id: canonicalProjectId,
    codicent: canonicalProjectId,
  };
}

export function normalizeProjectModel(project: Project, fallbackProjectId?: string): Project {
  const normalizedProject = normalizeProjectIdentity(project, fallbackProjectId) as Project;
  const clonedProject = JSON.parse(JSON.stringify(normalizedProject)) as Project;

  if (clonedProject.workspaces?.submenus) {
    let roleIdMap: Record<string, string> = {};

    clonedProject.workspaces.submenus = clonedProject.workspaces.submenus.map((submenu) => {
      const normalizedSubmenu = submenu as WorkspaceSubmenu;

      if (normalizedSubmenu.tabs) {
        // Normalize domain tab IDs to DOM-xxx format
        const usedDomainIds = new Set<string>();
        let nextDomainId = 1;
        normalizedSubmenu.tabs = (normalizedSubmenu.tabs as Tab[]).map((tab) => {
          const tabId = typeof tab?.id === "string" && /^DOM-\d{3}$/.test(tab.id)
            ? tab.id
            : (() => {
                let id;
                do {
                  id = `DOM-${String(nextDomainId).padStart(3, "0")}`;
                  nextDomainId++;
                } while (usedDomainIds.has(id));
                usedDomainIds.add(id);
                return id;
              })();
          // Normalize processes inside tab
          if (tab.data?.processes) {
            tab.data.processes = normalizeProcessCollection(tab.data.processes, normalizedSubmenu.tabs as Tab[]);
          }
          return {
            ...tab,
            id: tabId,
          };
        });
      }

      if (normalizedSubmenu.id === "userRoles") {
        roleIdMap = normalizeUserRolesSubmenu(normalizedSubmenu);
      }

      if (normalizedSubmenu.id === "glossary") {
        normalizeGlossarySubmenu(normalizedSubmenu);
      }

      return submenu;
    });

    clonedProject.workspaces.submenus = clonedProject.workspaces.submenus.map((submenu) => {
      const normalizedSubmenu = submenu as WorkspaceSubmenu;

      if (normalizedSubmenu.id === "stakeholders") {
        normalizeStakeholdersSubmenu(normalizedSubmenu, roleIdMap);
      }

      return submenu;
    });
  }

  if (Array.isArray(clonedProject.glossary)) {
    clonedProject.glossary = normalizeCollectionIds(clonedProject.glossary as Glossary[], "TERM").items;
  }

  return clonedProject;
}

function normalizeUserRolesSubmenu(submenu: WorkspaceSubmenu): Record<string, string> {
  const roles = Array.isArray(submenu.data?.userRoles) ? submenu.data.userRoles : [];
  const { items, idMap } = normalizeCollectionIds(roles, "ROLE");

  submenu.data = {
    ...(submenu.data ?? {}),
    userRoles: items,
  };

  return idMap;
}

function normalizeStakeholdersSubmenu(
  submenu: WorkspaceSubmenu,
  roleIdMap: Record<string, string>
): void {
  const stakeholders = Array.isArray(submenu.data?.data) ? submenu.data.data : [];
  const { items } = normalizeCollectionIds(stakeholders, "STK");

  submenu.data = {
    ...(submenu.data ?? {}),
    data: items.map((stakeholder) => mapStakeholderRoleId(stakeholder as Stakeholder, roleIdMap)),
  };
}

function normalizeGlossarySubmenu(submenu: WorkspaceSubmenu): void {
  const glossaryItems = Array.isArray(submenu.data)
    ? submenu.data
    : Array.isArray(submenu.data?.data)
      ? submenu.data.data
      : [];
  const { items } = normalizeCollectionIds(glossaryItems, "TERM");

  submenu.data = Array.isArray(submenu.data)
    ? items
    : {
      ...(submenu.data ?? {}),
      data: items,
    };
}

function mapStakeholderRoleId(
  stakeholder: Stakeholder,
  roleIdMap: Record<string, string>
): Stakeholder {
  if (typeof stakeholder.role !== "string" || !roleIdMap[stakeholder.role]) {
    return stakeholder;
  }

  return {
    ...stakeholder,
    role: roleIdMap[stakeholder.role],
  };
}
